import { Button, Dropdown, formatNumber, Tooltip } from '@cma/common'
import { InfoIcon, LineChartIcon } from '@cma/icons'
import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import {
  getAllMlses,
  getCurrentMonth,
  getPastMonths,
  getStatsByMonth,
  MonthAbbreviations
} from '../../broker/utils'
import { useAdminStats } from './useAdminStats'

const features = [
  { label: 'CMA', key: 'cmaActiveUsers' },
  { label: 'Buyer Tour', key: 'buyerActiveUsers' },
  { label: 'Property Report', key: 'propertyActiveUsers' },
  { label: 'Flyer', key: 'flyerActiveUsers' },
  { label: 'Homebeat', key: 'homebeatActiveUsers' }
] as const

interface UsageStatsProps {
  brokerageId?: string
  officeId?: string
}

export function UsageStats({ brokerageId, officeId }: UsageStatsProps) {
  return (
    <Suspense fallback={<UsageStatsSkeleton />}>
      <div className="flex flex-col">
        <UsageStats_ brokerageId={brokerageId} officeId={officeId} />
      </div>
    </Suspense>
  )
}

function UsageStats_({ brokerageId, officeId }: UsageStatsProps) {
  const { data: { stats: adminStats } = {} } = useAdminStats({
    brokerageId,
    officeId
  })

  const pastMonths = getPastMonths()
  const currentMonth = getCurrentMonth()

  const stats = useMemo(
    () => adminStats?.platformClient?.stats.monthlyStats || [],
    [adminStats?.platformClient?.stats.monthlyStats]
  )

  const allMlses = getAllMlses(stats)

  const [tooltipVisibilityMonth, setTooltipVisibilityMonth] = useState('')
  const [activeMonth, setActiveMonth] = useState(currentMonth)
  const [selectedMlses, setSelectedMlses] = useState<string[]>([])

  useEffect(() => {
    setSelectedMlses(
      allMlses.map((mls) => mls.key).filter((key): key is string => !!key)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokerageId, officeId, setSelectedMlses])

  const statsByMonth = useMemo(
    () => getStatsByMonth(stats, selectedMlses),
    [stats, selectedMlses]
  )

  const maxTotalUsers = useMemo(() => {
    return Math.max(
      0,
      ...Object.values(statsByMonth).map((m) => m?.totalUsers || 0)
    )
  }, [statsByMonth])

  const totalYAxis = Math.max(0, Math.min(maxTotalUsers, 4))
  const activeMonthStats = statsByMonth[activeMonth]

  if (!maxTotalUsers) {
    return (
      <div className="mx-auto flex flex-1 flex-col items-center justify-center py-6 md:max-w-[407px]">
        <div className="mb-[16px] flex h-[56px] w-[56px] items-center justify-center rounded-full bg-brand-white">
          <LineChartIcon className="text-brand-primary" />
        </div>
        <h2 className="helix-heading mb-1">Usage</h2>
        <p className="helix-body text-center text-gray-500">
          No usage yet, check back in a month. See product usage over time,
          export reports, and more.
        </p>
        <div className="mt-6 flex items-center gap-2">
          <Button variant="ghost">Learn more</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col divide-x rounded-lg bg-white md:flex-row md:shadow-raised">
      <div className="pb-3 md:w-9/12 md:px-4 md:py-3">
        <div className="mb-4 flex items-center justify-between md:mb-1.5">
          <div className="flex items-center gap-3 space-x-2 md:items-center md:gap-0">
            <h2 className="flex items-center space-x-1 font-semibold text-gray-900">
              <span>Active users</span>
              <Tooltip
                content={`Active users that have created at least one report in ${activeMonth}.`}>
                <button>
                  <InfoIcon className="h-4 w-4 text-gray-400" />
                </button>
              </Tooltip>
            </h2>
            <div className="flex gap-1 font-semibold text-gray-500">
              <span>{activeMonth}</span>{' '}
              <span className="helix-text-brand">
                {activeMonthStats?.totalActiveUsers || 0}
              </span>
              /<span>{activeMonthStats?.totalUsers || 0}</span>
            </div>
          </div>
          <div className="w-40">
            <Dropdown
              multiple
              disabled={allMlses.length <= 1}
              value={selectedMlses}
              onChange={setSelectedMlses}>
              <Dropdown.Button>
                {(() => {
                  if (selectedMlses.length === 0) return 'No MLSs Selected'
                  if (selectedMlses.length === allMlses.length)
                    return 'All MLSs'
                  return allMlses
                    .filter((mls) => selectedMlses.includes(mls.key || ''))
                    .map((mls) => mls.title)
                    .join(', ')
                })()}
              </Dropdown.Button>
              {allMlses.map((mls) => (
                <Dropdown.Option key={mls.key} value={mls.key || ''}>
                  {mls.title}
                </Dropdown.Option>
              ))}
            </Dropdown>
          </div>
        </div>
        <div className="mt-4 flex h-[8.5rem]">
          {!!selectedMlses.length && (
            <div className="mr-3 flex flex-shrink-0 flex-col justify-between text-right font-mono text-[0.625rem] leading-none text-gray-600">
              {new Array(totalYAxis + 1)
                .fill('')
                .map((_, i) => Math.ceil(maxTotalUsers * (i * 0.25)))
                .reverse()
                .map((val) => (
                  <div key={val}>{val}</div>
                ))}
            </div>
          )}
          <div className="-mt-0.5 flex w-full justify-between space-x-2 !border-b !border-solid !border-gray-100 lg:space-x-4 xl:space-x-1.5">
            {pastMonths.map((month) => {
              const mStats = statsByMonth[month]
              return (
                <button
                  key={month}
                  className="relative z-0 w-5 text-left"
                  onMouseOver={() => {
                    setActiveMonth(month)
                    setTooltipVisibilityMonth(month)
                  }}
                  onFocus={() => {
                    setActiveMonth(month)
                    setTooltipVisibilityMonth(month)
                  }}
                  onMouseLeave={() => setTooltipVisibilityMonth('')}
                  onBlur={() => setTooltipVisibilityMonth('')}>
                  <div
                    style={{
                      height: `100%`
                    }}
                    className="absolute bottom-0 z-10 w-full rounded-t bg-gray-200 transition-all"
                  />
                  <Tooltip
                    visible={tooltipVisibilityMonth === month}
                    content={`${
                      mStats?.totalActiveUsers || 0
                    } active users out of ${mStats?.totalUsers || 0}`}>
                    <div
                      style={{
                        height: `${
                          ((mStats?.totalActiveUsers || 0) / maxTotalUsers) *
                          100
                        }%`
                      }}
                      className={`absolute bottom-0 z-10 w-full rounded-t transition-all ${
                        activeMonth === month
                          ? 'bg-brand-primary'
                          : 'bg-gray-400'
                      }`}
                    />
                  </Tooltip>
                </button>
              )
            })}
          </div>
        </div>
        <div className="mt-3 flex">
          <div className="mr-3 flex-col text-transparent">{maxTotalUsers}</div>
          <div className="-ml-1.5 flex w-full justify-between space-x-2 lg:space-x-4 xl:space-x-4">
            {pastMonths.map((month) => (
              <div
                key={month}
                className="w-7 text-center font-mono text-[0.625rem] text-gray-600 hover:cursor-pointer"
                onMouseOver={() => {
                  setActiveMonth(month)
                  setTooltipVisibilityMonth(month)
                }}
                onFocus={() => {
                  setActiveMonth(month)
                  setTooltipVisibilityMonth(month)
                }}
                onMouseLeave={() => setTooltipVisibilityMonth('')}
                onBlur={() => setTooltipVisibilityMonth('')}>
                {MonthAbbreviations[month]}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="space-y-3 py-3 md:w-4/12 md:!border-l md:!border-solid md:border-gray-200 md:px-4">
        <h2 className="flex items-center space-x-1 font-semibold text-gray-900">
          <span>Feature usage</span>
          <Tooltip content={`Active users by features in ${activeMonth}.`}>
            <button>
              <InfoIcon className="h-4 w-4 text-gray-400" />
            </button>
          </Tooltip>
        </h2>
        <div className="space-y-1.5">
          <div className="helix-small flex items-center justify-between text-gray-500">
            <span>Feature</span>
            <span>Active Users</span>
          </div>
          {features.map(({ label, key }) => (
            <div key={key} className="flex items-end space-x-1.5">
              <div className="w-full space-y-0.5">
                <div className="helix-small leading-4">{label}</div>
                <div
                  className="h-1 max-w-[92%] rounded-full bg-gray-400 transition-all"
                  style={{
                    width: activeMonthStats?.totalActiveUsers
                      ? `${Math.min(
                          (((activeMonthStats?.[key] as number) || 0) /
                            (activeMonthStats?.totalActiveUsers || 1)) *
                            100,
                          100
                        )}%`
                      : '0%'
                  }}
                />
              </div>
              <div className="helix-small flex-shrink-0 leading-none">
                {formatNumber((activeMonthStats?.[key] as number) || 0)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function UsageStatsSkeleton() {
  const pastMonths = getPastMonths()
  const fakeMonthStats = useRef<number[]>(
    new Array(12)
      .fill('')
      .map(() => Math.min(Math.max(Math.floor(Math.random() * 100), 40), 90))
  )
  const fakeReportStats = useRef<number[]>(
    new Array(5)
      .fill('')
      .map(() => Math.min(Math.max(Math.floor(Math.random() * 100), 40), 90))
  )

  return (
    <div className="flex w-full flex-col divide-x rounded-lg bg-white md:flex-row md:shadow-raised">
      <div className="pb-3 md:w-9/12 md:px-4 md:py-3">
        <div className="flex items-center justify-between">
          <div className="mb-4 flex items-center gap-3 space-x-2 md:mb-1.5 md:items-center md:gap-0">
            <h2 className="flex items-center space-x-1 font-semibold text-gray-900">
              <span>Active users</span>
              <Tooltip content="">
                <button>
                  <InfoIcon className="h-4 w-4 text-gray-400" />
                </button>
              </Tooltip>
            </h2>
            <div className="flex gap-1 font-semibold text-gray-500">
              <div className="h-4 w-14 rounded bg-gray-200" />
            </div>
          </div>
          <div className="w-40">
            <Dropdown multiple value={[]} onChange={() => {}}>
              <Dropdown.Button>No MLSs Selected</Dropdown.Button>
            </Dropdown>
          </div>
        </div>
        <div className="helix-border-bottom helix-border-bottom--gray-100 mt-3 flex h-[8.5rem] animate-pulse">
          <div
            aria-hidden
            className="mr-4 hidden flex-shrink-0 flex-col justify-between text-right font-mono text-[0.625rem] leading-none text-transparent md:flex">
            0
          </div>
          <div className="-mt-0.5 flex w-full justify-between space-x-2 border-b border-gray-100 lg:space-x-4 xl:space-x-2">
            {fakeMonthStats.current.map((stat, index) => (
              <div key={index} className="relative z-0 w-5 text-left">
                <div
                  style={{
                    height: `${stat}%`
                  }}
                  className="absolute bottom-0 z-10 w-full rounded-t bg-gray-300"
                />
                <div className="absolute bottom-0 left-0 h-full w-full rounded-t bg-gray-200" />
              </div>
            ))}
          </div>
        </div>
        <div className="mt-3 flex animate-pulse">
          <div
            aria-hidden
            className="mr-4 hidden flex-col justify-between text-right font-mono text-[0.625rem] leading-none text-transparent md:flex">
            0
          </div>
          <div className="flex w-full justify-between space-x-2 lg:space-x-4 xl:space-x-2">
            {pastMonths.map((month, index) => (
              <div key={index} className="h-3 w-[25px] rounded bg-gray-200" />
            ))}
          </div>
        </div>
      </div>
      <div className="space-y-3 py-3 md:w-4/12 md:!border-l md:!border-solid md:border-gray-200 md:px-4">
        <h2 className="flex items-center space-x-1 font-semibold text-gray-900">
          <span>Feature usage</span>
          <Tooltip content="">
            <button>
              <InfoIcon className="h-4 w-4 text-gray-400" />
            </button>
          </Tooltip>
        </h2>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between space-x-2">
            <h3 className="helix-small text-gray-500">Feature</h3>
            <h3 className="helix-small text-gray-500">Active Users</h3>
          </div>
          <div className="animate-pulse space-y-2.5">
            {fakeReportStats.current.map((width, index) => (
              <div key={index} className="flex items-end space-x-1.5">
                <div className="w-full space-y-0.5">
                  <div className="h-3 w-24 rounded bg-gray-200" />
                  <div
                    className="h-1 max-w-[92%] rounded-full bg-gray-300"
                    style={{ width: `${width}%` }}
                  />
                </div>
                <div className="h-3 w-4 rounded bg-gray-200" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
